import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Eye, Smartphone, FileSignature, CreditCard, BadgeCheck, AlertCircle, Sparkles, Zap, CheckCircle } from 'lucide-react';
import BackButton from '../components/BackButton';
import { useAvenantWorkflow } from './useAvenantWorkflow';
import AvenantWorkflowSteps from './AvenantWorkflowSteps';

// Étapes du workflow
const WORKFLOW_STEPS = [
  { id: 'preview', label: 'Prévisualisation', icon: Eye, color: 'from-violet-500 to-purple-600' },
  { id: 'sms', label: 'Validation SMS', icon: Smartphone, color: 'from-blue-500 to-cyan-500' },
  { id: 'signature', label: 'Signature', icon: FileSignature, color: 'from-emerald-500 to-teal-500' },
  { id: 'payment', label: 'Paiement', icon: CreditCard, color: 'from-amber-500 to-orange-500' },
  { id: 'confirmation', label: 'Terminé', icon: BadgeCheck, color: 'from-green-500 to-emerald-500' }
];

export default function AvenantWorkflow() {
  const wf = useAvenantWorkflow();
  const { navigate, currentStep, setCurrentStep, avenantId, avenantRef, contratParent } = wf;

  // Vérification données
  if (!avenantId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L3N2Zz4=')] opacity-20"></div>
        <Card className="max-w-md w-full bg-white/10 backdrop-blur-xl border-white/20 shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <AlertCircle className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Session expirée</h2>
            <p className="text-white/70 mb-6">Créez un avenant depuis le simulateur BATICO</p>
            <Button
              onClick={() => navigate('/calculateur-batico')}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg shadow-purple-500/25"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Simulateur BATICO
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Background Pattern */}
      <div className="fixed inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDUpIiBzdHJva2Utd2lkdGg9IjIiLz48L2c+PC9zdmc+')] opacity-30 pointer-events-none"></div>

      {/* Animated Gradient Orbs */}
      <div className="fixed top-20 left-20 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse pointer-events-none"></div>
      <div className="fixed bottom-20 right-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" style={{animationDelay: '1s'}}></div>

      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/10 backdrop-blur-xl bg-black/20">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              {currentStep > 0 ? (
                <Button
                  variant="ghost"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="text-white/70 hover:text-white hover:bg-white/10"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Retour à l'étape {currentStep}
                </Button>
              ) : (
                <BackButton variant="dark" label="Retour" />
              )}

              <div className="flex items-center gap-3">
                <div className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500/20 to-purple-500/20 border border-violet-500/30">
                  <Sparkles className="h-4 w-4 text-violet-400" />
                  <span className="text-sm font-medium text-violet-300">{avenantRef || 'Nouvel Avenant'}</span>
                </div>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  <Zap className="h-3 w-3 mr-1" />
                  En ligne
                </Badge>
              </div>
            </div>
          </div>
        </header>

        {/* Progress Steps */}
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-12">
            {WORKFLOW_STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;

              return (
                <div key={step.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center relative">
                    {/* Step Circle */}
                    <div className={`
                      relative w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-500
                      ${isCompleted
                        ? 'bg-gradient-to-br from-green-400 to-emerald-500 shadow-lg shadow-green-500/30'
                        : isActive
                          ? `bg-gradient-to-br ${step.color} shadow-lg shadow-purple-500/30 ring-4 ring-white/20`
                          : 'bg-white/10 border border-white/20'
                      }
                    `}>
                      {isCompleted ? (
                        <CheckCircle className="h-7 w-7 text-white" />
                      ) : (
                        <StepIcon className={`h-6 w-6 ${isActive ? 'text-white' : 'text-white/50'}`} />
                      )}

                      {/* Pulse Animation for Active */}
                      {isActive && (
                        <div className="absolute inset-0 rounded-2xl bg-white/20 animate-ping"></div>
                      )}
                    </div>

                    {/* Label */}
                    <span className={`
                      mt-3 text-sm font-medium transition-colors
                      ${isActive ? 'text-white' : isCompleted ? 'text-green-400' : 'text-white/50'}
                    `}>
                      {step.label}
                    </span>
                  </div>

                  {/* Connector Line */}
                  {index < WORKFLOW_STEPS.length - 1 && (
                    <div className="flex-1 mx-4 h-1 rounded-full overflow-hidden bg-white/10">
                      <div
                        className={`h-full transition-all duration-700 ${isCompleted ? 'bg-gradient-to-r from-green-400 to-emerald-500 w-full' : 'w-0'}`}
                      ></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Mention avenant jusqu'au paiement */}
          {currentStep <= 3 && (
            <div className="mb-6 rounded-xl border border-violet-400/40 bg-violet-500/15 px-4 py-3 text-sm text-violet-100" data-testid="avenant-workflow-mention">
              <strong>Avenant {avenantRef ? String(avenantRef).split('/')[0] : ''}</strong>
              {contratParent?.numero_police && <> au contrat <span className="font-mono">{contratParent.numero_police}</span></>}
              {' '}— effet du {new Date().toLocaleDateString('fr-FR')}
              {(contratParent?.date_fin || contratParent?.periode_adhesion_fin) && <> au {new Date(contratParent.date_fin || contratParent.periode_adhesion_fin).toLocaleDateString('fr-FR')} (fin du contrat parent)</>}
            </div>
          )}

          {/* Step Content */}
          <AvenantWorkflowSteps {...wf} />
        </div>
      </div>
    </div>
  );
}
